// Synexis: passo 2 do wizard "Novo documento" — experiência agenda-first (estilo ClickSign).
// Um único <wizard-signers> envolve o form e o <dialog>. A página nunca abre com formulário:
// o signatário entra pela agenda (multi-seleção + abas Todos/Favoritos), por "Signatário novo"
// (cadastro no modal, exceção) ou por "Eu também assino" — e todos passam pelo painel
// "Assinar como" (papel OBRIGATÓRIO + canal). Os signatários confirmados viram cards-resumo
// e hidden inputs `signers[][...]` regenerados na ordem dos cards (contrato do POST intacto).
// Estados do modal: 'agenda' | 'new' | 'assign'. Edição de um card reabre 'assign' pelo uid.
export default class extends HTMLElement {
  connectedCallback () {
    this.signers = []
    this.pending = []
    this.editingUid = null
    this.uidSeq = 0
    this.tab = 'all'
    this.selected = new Map()
    this.contacts = []

    this.dialog = this.querySelector('dialog')
    this.searchInput = this.querySelector('[data-signers-search]')
    this.resultsEl = this.querySelector('[data-signers-results]')
    this.assignRowsEl = this.querySelector('[data-assign-rows]')
    this.cardsEl = this.querySelector('[data-signers-cards]')
    this.emptyEl = this.querySelector('[data-signers-empty]')
    this.inputsEl = this.querySelector('[data-signers-inputs]')
    this.submitBtn = this.querySelector('[data-signers-submit]')
    this.advanceBtn = this.querySelector('[data-signers-advance]')
    this.confirmBtn = this.querySelector('[data-signers-confirm]')
    this.orderHintEl = this.querySelector('[data-signers-order-hint]')

    // Restore de snapshot do Turbo (voltar do navegador): os hidden inputs restaurados
    // são a fonte de verdade — reidratamos this.signers a partir deles em vez de
    // descartar o trabalho do usuário. render() abaixo regenera cards e inputs.
    this.rehydrate()

    this.addEventListener('click', this.onClick)
    this.addEventListener('change', this.onChange)
    this.addEventListener('input', this.onInput)
    this.searchInput?.addEventListener('input', this.onSearch)

    this.render()

    // Atalho "Assine voce mesmo" (selfsign=1): o passo 2 abre direto no painel
    // "Assinar como" com o remetente e canal Link. So com a lista vazia — no back
    // do navegador os hidden inputs restaurados (rehydrate) vencem o atalho.
    if (this.dataset.autoSelfsign === 'true' && this.signers.length === 0) this.selfSign('link')
  }

  disconnectedCallback () {
    this.removeEventListener('click', this.onClick)
    this.removeEventListener('change', this.onChange)
    this.removeEventListener('input', this.onInput)
    this.searchInput?.removeEventListener('input', this.onSearch)
    clearTimeout(this.debounce)
  }

  get requireCpf () { return this.dataset.requireCpf === 'true' }
  get methodLabels () { return JSON.parse(this.dataset.methodLabels || '{}') }

  // "Em sequência" marcado? A ordem dos cards só ganha affordances (numeral, handle,
  // setas, drag) nesse modo — em paralelo a ordem é irrelevante e a UI enganaria.
  get sequential () { return this.querySelector('[name="preserve_order"][value="1"]')?.checked === true }

  // Esconder SÓ com o atributo hidden não basta (achado do Codex): .btn (daisyUI) e
  // .flex (utility) têm display próprio que vence o [hidden] da base do Tailwind — o
  // inline style ganha de todos, sem depender da ordem das camadas de CSS.
  toggleSlot (el, show) {
    if (!el) return

    el.hidden = !show
    el.style.display = show ? '' : 'none'
  }

  // Telefone p/ WhatsApp precisa de formato internacional (mesmo pattern do envio
  // clássico) — sem o "+", Whatsapp.normalize_number prefixaria 55 num número estrangeiro.
  // Fonte única do formato: lib/synexis/phone.rb (HTML_PATTERN/valid?) — manter em sincronia.
  static INTL_PHONE = /^\+[0-9\s-]+$/

  // Chave de dedupe/seleção: e-mail quando existe; linhas do fallback de submitters
  // podem vir SEM e-mail (envios phone-only) e não podem colidir todas em ''.
  contactKey (item) {
    return (item.email || '').toLowerCase() || `noemail:${[item.name, item.phone].join('|').toLowerCase()}`
  }

  signerKeys () { return new Set(this.signers.map((s) => this.contactKey(s))) }

  // Fim do arraste: limpa realce E contador de TODOS os cards. O dragend so dispara no card
  // de origem, entao sem esta varredura o contador de um card sobrevoado ficaria positivo e
  // o realce dele ficaria preso no arraste seguinte.
  clearDragOver () {
    this.cardsEl.querySelectorAll('[data-index]').forEach((el) => {
      el.classList.remove('drag-over')
      delete el.dataset.dragDepth
    })
  }

  rehydrate () {
    const groups = [...(this.inputsEl?.querySelectorAll('[data-signer-inputs]') || [])]

    this.signers = groups.map((group) => {
      const signer = { uid: `s${++this.uidSeq}` };

      ['name', 'email', 'cpf', 'phone', 'empresa', 'role', 'method'].forEach((field) => {
        signer[field] = group.querySelector(`[name="signers[][${field}]"]`)?.value || ''
      })

      return signer
    }).filter((s) => s.name || s.email)
  }

  onClick = (e) => {
    const actionEl = e.target.closest('[data-signers-action]')

    if (actionEl && this.contains(actionEl)) {
      e.preventDefault()
      this.runAction(actionEl.dataset.signersAction, actionEl)
    }
  }

  runAction (action, el) {
    if (action === 'open-agenda') this.openAgenda()
    if (action === 'open-new') this.openNew()
    if (action === 'self-sign') this.selfSign()
    if (action === 'back') this.goBack(el)
    if (action === 'advance') this.advanceFromAgenda()
    if (action === 'new-advance') this.advanceFromNew()
    if (action === 'confirm') this.confirmAssign()
    if (action === 'edit') this.editSigner(el.closest('[data-uid]').dataset.uid)
    if (action === 'remove') this.removeSigner(el.closest('[data-uid]').dataset.uid)
    if (action === 'move-up') this.moveSigner(el.closest('[data-uid]').dataset.uid, -1)
    if (action === 'move-down') this.moveSigner(el.closest('[data-uid]').dataset.uid, 1)
  }

  // Alternativa acessível/touch ao drag (HTML5 drag não existe no iOS Safari).
  moveSigner (uid, delta) {
    const from = this.signers.findIndex((s) => s.uid === uid)
    const to = from + delta

    if (from < 0 || to < 0 || to >= this.signers.length) return

    const [moved] = this.signers.splice(from, 1)

    this.signers.splice(to, 0, moved)
    this.render()
  }

  // "Voltar" é contextual ao painel: no cadastro novo fecha o modal (é porta de
  // entrada); no "Assinar como" retorna ao painel de origem — ou fecha quando a
  // origem não é um painel (edição de card / eu também assino).
  goBack (el) {
    const panel = el.closest('[data-panel]')?.dataset.panel

    if (panel === 'assign' && !this.editingUid && this.cameFrom) {
      this.showPanel(this.cameFrom)
      if (this.cameFrom === 'agenda') this.renderContacts()

      return
    }

    this.dialog.close()
  }

  // ── Painéis ──────────────────────────────────────────────────────────────
  showPanel (name) {
    if (!name) { this.dialog.close(); return }

    this.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== name })

    if (!this.dialog.open) { this.dialog.inert = false; this.dialog.showModal() }
  }

  openAgenda () {
    this.editingUid = null
    this.cameFrom = 'agenda'
    this.selected = new Map()
    this.tab = 'all'
    const allTab = this.querySelector('[data-signers-tab][value="all"]')
    if (allTab) allTab.checked = true
    if (this.searchInput) this.searchInput.value = ''

    this.showPanel('agenda')
    this.load('')
    this.searchInput?.focus()
  }

  openNew () {
    this.editingUid = null
    this.cameFrom = 'new'
    this.querySelectorAll('[data-new-field]').forEach((i) => { i.value = '' })
    this.setNewError('')
    this.showPanel('new')
    this.querySelector('[data-new-field="name"]')?.focus()
  }

  // method: canal default do slot do remetente. O botao "Eu tambem assino" mantem
  // 'email'; o atalho auto-armado usa 'link' — convite por e-mail a si mesmo e ruido.
  selfSign (method = 'email') {
    const email = (this.dataset.selfEmail || '').toLowerCase()
    const existing = this.signers.find((s) => s.email.toLowerCase() === email)

    if (existing) { this.editSigner(existing.uid); return }

    // Perfil sem nome (permitido no cadastro): cai no painel de cadastro com
    // e-mail/CPF pré-preenchidos para o remetente informar o nome — o slot de
    // assinatura nunca pode ficar sem nome.
    if (!(this.dataset.selfName || '').trim()) {
      this.openNew()
      this.querySelector('[data-new-field="email"]').value = this.dataset.selfEmail || ''
      this.querySelector('[data-new-field="phone"]').value = this.dataset.selfPhone || ''
      this.querySelector('[data-new-field="cpf"]').value = this.dataset.selfCpf || ''

      return
    }

    this.editingUid = null
    this.cameFrom = null
    this.pending = [{
      name: this.dataset.selfName || '',
      email: this.dataset.selfEmail || '',
      cpf: this.dataset.selfCpf || '',
      phone: this.dataset.selfPhone || '',
      empresa: '',
      role: '',
      method
    }]
    this.renderAssign()
    this.showPanel('assign')
  }

  editSigner (uid) {
    const signer = this.signers.find((s) => s.uid === uid)

    if (!signer) return

    this.editingUid = uid
    this.cameFrom = null
    this.pending = [{ ...signer }]
    this.renderAssign()
    this.showPanel('assign')
  }

  removeSigner (uid) {
    this.signers = this.signers.filter((s) => s.uid !== uid)
    this.render()
  }

  // ── Agenda ───────────────────────────────────────────────────────────────
  onSearch = () => {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.load(this.searchInput.value), 200)
  }

  load (q) {
    // Token de sequência: a última busca PEDIDA vence — sem ele, uma resposta
    // lenta de q anterior sobrescreveria a lista atual (multi-seleção errada).
    const seq = (this.loadSeq = (this.loadSeq || 0) + 1)

    fetch('/submitters_autocomplete?' + new URLSearchParams({ q }))
      .then((resp) => resp.json())
      .then((items) => {
        if (seq !== this.loadSeq) return

        this.contacts = items
        this.renderContacts()
      })
      .catch(() => {
        if (seq !== this.loadSeq) return

        this.contacts = []
        this.renderContacts()
      })
  }

  renderContacts () {
    if (!this.resultsEl) return

    this.resultsEl.replaceChildren()

    const items = this.tab === 'favorites' ? this.contacts.filter((c) => c.favorite) : this.contacts

    if (!items.length) {
      const empty = document.createElement('div')

      empty.className = 'p-4 text-sm text-base-content/60 text-center'
      empty.textContent = this.tab === 'favorites'
        ? (this.dataset.noFavoritesText || '')
        : (this.dataset.emptyText || '')
      this.resultsEl.appendChild(empty)
      this.updateAdvance()

      return
    }

    const signerKeys = this.signerKeys()

    items.forEach((item) => {
      const key = this.contactKey(item)
      const already = signerKeys.has(key)

      const row = document.createElement('label')

      row.className = `flex items-center gap-3 p-2.5 rounded-xl ${already ? 'opacity-50' : 'cursor-pointer hover:bg-[color:var(--color-surface-subtle)]'}`

      const checkbox = document.createElement('input')

      checkbox.type = 'checkbox'
      checkbox.className = 'base-checkbox'
      checkbox.setAttribute('data-contact-checkbox', '')
      checkbox.dataset.contact = JSON.stringify(item)
      checkbox.disabled = already
      checkbox.checked = this.selected.has(key)

      const info = document.createElement('span')

      info.className = 'flex flex-col min-w-0'
      info.dir = 'auto'

      const name = document.createElement('span')

      name.className = 'font-medium text-sm truncate'
      name.textContent = item.name || item.email

      const detail = document.createElement('span')

      detail.className = 'text-xs text-base-content/60 truncate'
      detail.textContent = already
        ? (this.dataset.alreadyAddedText || '')
        : [item.email, item.cpf, item.empresa].filter(Boolean).join('   ·   ')

      info.append(name, detail)
      row.append(checkbox, info)

      if (item.favorite) {
        const star = document.createElement('span')

        star.className = 'ml-auto text-[color:var(--color-accent-fg)] text-xs'
        star.textContent = '★'
        row.appendChild(star)
      }

      this.resultsEl.appendChild(row)
    })

    this.updateAdvance()
  }

  onChange = (e) => {
    // Alternar a ordem de assinatura liga/desliga as affordances de reordenação dos cards.
    if (e.target.name === 'preserve_order') {
      this.render()

      return
    }

    const tab = e.target.closest('[data-signers-tab]')

    if (tab) {
      this.tab = tab.value
      this.renderContacts()

      return
    }

    const checkbox = e.target.closest('[data-contact-checkbox]')

    if (checkbox) {
      const contact = JSON.parse(checkbox.dataset.contact)
      const key = this.contactKey(contact)

      if (checkbox.checked) this.selected.set(key, contact)
      else this.selected.delete(key)

      this.updateAdvance()

      return
    }

    if (e.target.closest('[data-assign-role], [data-assign-method]')) this.syncAssignRows()
  }

  onInput = (e) => {
    if (e.target.closest('[data-assign-cpf], [data-assign-phone]')) this.updateConfirm()
  }

  updateAdvance () {
    if (!this.advanceBtn) return

    const count = this.selected.size

    this.advanceBtn.disabled = count === 0
    this.advanceBtn.textContent = count > 1 ? `${this.advanceBtn.dataset.label} (${count})` : this.advanceBtn.dataset.label
  }

  advanceFromAgenda () {
    if (!this.selected.size) return

    this.pending = [...this.selected.values()].map((c) => ({
      name: c.name || '',
      email: c.email || '',
      cpf: c.cpf || '',
      phone: c.phone || '',
      empresa: c.empresa || '',
      role: '',
      method: 'email'
    }))
    this.renderAssign()
    this.showPanel('assign')
  }

  // ── Signatário novo ──────────────────────────────────────────────────────
  newFieldValue (field) { return (this.querySelector(`[data-new-field="${field}"]`)?.value || '').trim() }

  setNewError (message) {
    const el = this.querySelector('[data-new-error]')

    if (el) { el.textContent = message; el.hidden = !message }
  }

  advanceFromNew () {
    const name = this.newFieldValue('name')
    const email = this.newFieldValue('email')
    const cpf = this.newFieldValue('cpf')

    if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setNewError(this.dataset.invalidFieldsText || ''); return
    }
    if (this.requireCpf && cpf.replace(/\D/g, '').length !== 11) {
      this.setNewError(this.dataset.invalidFieldsText || ''); return
    }
    if (this.signers.some((s) => s.email.toLowerCase() === email.toLowerCase())) {
      this.setNewError(this.dataset.alreadyAddedText || ''); return
    }

    this.setNewError('')
    this.pending = [{
      name,
      email,
      cpf,
      phone: this.newFieldValue('phone'),
      empresa: this.newFieldValue('empresa'),
      role: '',
      method: 'email'
    }]
    this.renderAssign()
    this.showPanel('assign')
  }

  // ── Assinar como ─────────────────────────────────────────────────────────
  renderAssign () {
    if (!this.assignRowsEl) return

    this.assignRowsEl.replaceChildren()

    const template = this.querySelector('[data-assign-row-template]')

    this.pending.forEach((item, index) => {
      const row = template.content.firstElementChild.cloneNode(true)

      row.dataset.assignIndex = index
      row.querySelector('[data-slot="name"]').textContent = item.name || item.email
      row.querySelector('[data-slot="detail"]').textContent = [item.email, item.cpf, item.empresa].filter(Boolean).join('   ·   ')

      const roleSelect = row.querySelector('[data-assign-role]')

      if (item.role) roleSelect.value = item.role

      const methodSelect = row.querySelector('[data-assign-method]')

      // Se o canal gravado nao existe mais entre as opcoes (WhatsApp despareado depois que este
      // signatario foi definido), o <select> iria para selectedIndex = -1: some visualmente e o
      // confirmAssign le string vazia, rebaixando o signatario para e-mail SEM o usuario pedir,
      // so por ter aberto o "editar" para trocar o papel. Cai para 'email' de forma explicita.
      let effectiveMethod = item.method || 'email'

      if (methodSelect) {
        const supported = Array.from(methodSelect.options).some((o) => o.value === effectiveMethod)

        if (!supported) effectiveMethod = 'email'

        methodSelect.value = effectiveMethod
      }

      // CPF só é pedido aqui quando obrigatório e ausente (linhas do fallback de submitters).
      const cpfInput = row.querySelector('[data-assign-cpf]')

      if (cpfInput) cpfInput.hidden = !(this.requireCpf && !item.cpf)

      const phoneInput = row.querySelector('[data-assign-phone]')

      if (phoneInput) {
        phoneInput.value = item.phone || ''
        // Pelo canal EFETIVO, nao pelo gravado: com o WhatsApp indisponivel o seletor ja caiu
        // para e-mail, e olhar `item.method` cru deixava o campo de telefone visivel ao lado de
        // "E-mail" — e, vazio, o `updateConfirm` travava o botao Confirmar exigindo um numero
        // que o canal escolhido nem usa.
        phoneInput.hidden = effectiveMethod !== 'whatsapp'
      }

      this.assignRowsEl.appendChild(row)
    })

    this.updateConfirm()
  }

  syncAssignRows () {
    // Canal WhatsApp exige telefone: mostra o input quando o canal muda.
    this.assignRowsEl.querySelectorAll('[data-assign-row]').forEach((row) => {
      const method = row.querySelector('[data-assign-method]')?.value || 'email'
      const phoneInput = row.querySelector('[data-assign-phone]')

      if (phoneInput) phoneInput.hidden = method !== 'whatsapp'
    })

    this.updateConfirm()
  }

  updateConfirm () {
    if (!this.confirmBtn) return

    const rows = [...this.assignRowsEl.querySelectorAll('[data-assign-row]')]

    const valid = rows.length > 0 && rows.every((row) => {
      if (!row.querySelector('[data-assign-role]')?.value) return false

      const cpfInput = row.querySelector('[data-assign-cpf]')

      if (cpfInput && !cpfInput.hidden && cpfInput.value.replace(/\D/g, '').length !== 11) return false

      const phoneInput = row.querySelector('[data-assign-phone]')

      if (phoneInput && !phoneInput.hidden && !this.constructor.INTL_PHONE.test(phoneInput.value.trim())) return false

      return true
    })

    this.confirmBtn.disabled = !valid
  }

  confirmAssign () {
    const rows = [...this.assignRowsEl.querySelectorAll('[data-assign-row]')]

    const confirmed = rows.map((row, index) => {
      const item = this.pending[index]
      const cpfInput = row.querySelector('[data-assign-cpf]')
      const phoneInput = row.querySelector('[data-assign-phone]')

      return {
        ...item,
        uid: item.uid || `s${++this.uidSeq}`,
        role: row.querySelector('[data-assign-role]').value,
        method: row.querySelector('[data-assign-method]')?.value || 'email',
        cpf: (cpfInput && !cpfInput.hidden && cpfInput.value) || item.cpf || '',
        phone: (phoneInput && !phoneInput.hidden && phoneInput.value.trim()) || item.phone || ''
      }
    })

    if (this.editingUid) {
      this.signers = this.signers.map((s) => (s.uid === this.editingUid ? confirmed[0] : s))
      this.editingUid = null
    } else {
      this.signers = [...this.signers, ...confirmed]
    }

    this.pending = []
    this.dialog.close()
    this.render()
  }

  // ── Render da página (cards + hidden inputs) ─────────────────────────────
  render () {
    if (!this.cardsEl || !this.inputsEl) return

    this.cardsEl.replaceChildren()
    this.inputsEl.replaceChildren()

    const cardTemplate = this.querySelector('[data-signer-card-template]')
    const inputsTemplate = this.querySelector('[data-hidden-inputs-template]')

    const sequential = this.sequential

    this.signers.forEach((signer, index) => {
      const card = cardTemplate.content.firstElementChild.cloneNode(true)

      // HTML5 Drag API — reordenação de signatários (arrastar card). Só no modo
      // "Em sequência": com draggable=false o dragstart nunca dispara e os listeners
      // de drop ficam inertes.
      card.draggable = sequential
      card.dataset.index = index
      card.addEventListener('dragstart', (e) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', index.toString())
        card.classList.add('opacity-40')
      })
      // dragenter/dragleave borbulham dos filhos do card, entao o realce piscaria a cada
      // elemento interno que o ponteiro atravessasse. A guarda por e.relatedTarget nao
      // resolve no Chromium, que entrega null nesses eventos — contamos entradas e saidas
      // e so tiramos o realce quando o contador zera (o ponteiro saiu do card inteiro).
      //
      // O contador vive no dataset do ELEMENTO, nao numa variavel do closure: o dragend
      // dispara no card de ORIGEM e precisa zerar o contador dos OUTROS cards. Com estado
      // no closure, um arraste cancelado sobre outro card deixava aquele contador positivo
      // e o realce ficava preso no arraste seguinte (achado do Codex).
      card.addEventListener('dragend', () => {
        card.classList.remove('opacity-40')
        this.clearDragOver()
      })
      card.addEventListener('dragenter', (e) => {
        e.preventDefault()
        card.dataset.dragDepth = String((parseInt(card.dataset.dragDepth, 10) || 0) + 1)
        card.classList.add('drag-over')
      })
      card.addEventListener('dragleave', () => {
        const depth = (parseInt(card.dataset.dragDepth, 10) || 0) - 1

        card.dataset.dragDepth = String(Math.max(depth, 0))

        if (depth <= 0) card.classList.remove('drag-over')
      })
      card.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      })
      card.addEventListener('drop', (e) => {
        e.preventDefault()
        this.clearDragOver()
        const from = parseInt(e.dataTransfer.getData('text/plain'), 10)
        const to = index
        if (!isNaN(from) && from !== to) {
          const [moved] = this.signers.splice(from, 1)
          this.signers.splice(to, 0, moved)
          this.render()
        }
      })

      card.dataset.uid = signer.uid
      card.querySelector('[data-slot="name"]').textContent = signer.name || signer.email
      card.querySelector('[data-slot="role"]').textContent = signer.role
      card.querySelector('[data-slot="detail"]').textContent = [signer.email, signer.cpf, signer.empresa].filter(Boolean).join('   ·   ')

      const methodEl = card.querySelector('[data-slot="method"]')

      // O canal escolhido aparece SEMPRE. Escondia-se quando o WhatsApp estava indisponivel,
      // porque naquela epoca o seletor inteiro sumia junto e so sobrava "email". Hoje o seletor
      // e permanente (email/link) e so a opcao do WhatsApp e filtrada, entao esconder o rotulo
      // deixaria o card mudo sobre um canal que o usuario acabou de escolher.
      if (methodEl) methodEl.textContent = this.methodLabels[signer.method] || ''

      // Affordances de ordem (numeral + handle + setas) — visíveis só em "Em sequência".
      const orderEl = card.querySelector('[data-slot="order"]')

      if (orderEl) orderEl.textContent = String(index + 1)

      this.toggleSlot(orderEl, sequential)
      this.toggleSlot(card.querySelector('[data-slot="handle"]'), sequential)

      const upBtn = card.querySelector('[data-signers-action="move-up"]')
      const downBtn = card.querySelector('[data-signers-action="move-down"]')

      this.toggleSlot(upBtn, sequential)
      this.toggleSlot(downBtn, sequential)

      if (upBtn) upBtn.disabled = index === 0
      if (downBtn) downBtn.disabled = index === this.signers.length - 1

      this.cardsEl.appendChild(card)

      const inputs = inputsTemplate.content.firstElementChild.cloneNode(true);

      ['name', 'email', 'cpf', 'phone', 'empresa', 'role', 'method'].forEach((field) => {
        inputs.querySelector(`[name="signers[][${field}]"]`).value = signer[field] || ''
      })

      this.inputsEl.appendChild(inputs)
    })

    if (this.emptyEl) this.emptyEl.hidden = this.signers.length > 0
    if (this.submitBtn) this.submitBtn.disabled = this.signers.length === 0

    this.toggleSlot(this.orderHintEl, sequential && this.signers.length > 1)
  }
}
